import { apiRoutes } from '@/constants/api-routes';
import { apiRequest, type QueryParams } from './http';
import type { PaginatedBooks } from '@/types/api';
import type { ListBooksFilters } from './books.service';

/**
 * Typed wrapper over `GET /admin/books`. The endpoint is protected by the
 * server's `AdminGuard` (role lookup via the `get_user_role` RPC) and returns
 * the same `PaginatedBooks` shape as the public `/books` list — the only
 * difference is that admin results include books whose `status` is not yet
 * `'live'`, so the dashboard can surface drafts.
 *
 * The accepted filters intentionally mirror `ListBooksFilters` so the admin
 * table reuses the same label-type multi-selects as the public catalog. If
 * free-text search is added server-side later, extend this file rather than
 * forking into a separate service.
 */
export type AdminListBooksFilters = ListBooksFilters;

function toQueryParams(filters: AdminListBooksFilters): QueryParams {
  const query: QueryParams = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    query[key] = value as QueryParams[string];
  }
  return query;
}

export const adminBooksService = {
  /**
   * Paginated admin listing. Same `limit`/`offset` convention as
   * `booksService.list` — defaults are owned by the server (20 per page,
   * capped at 100 via `ListBooksQueryDto`).
   */
  list(filters: AdminListBooksFilters = {}) {
    return apiRequest<PaginatedBooks>(apiRoutes.adminBooks.list, {
      query: toQueryParams(filters),
    });
  },
};
